/**
 * In-app help center content — one guide per screen + the new-design basics.
 * `routes` = path prefixes the guide belongs to ("במסך הזה" + the "?" beside the screen title).
 * bodyHtml is static, authored here (no user content), so it is safe to render as-is.
 */
export type HelpArticle = {
  slug: string;
  title: string;
  summary: string;
  category: string;
  keywords: string[];
  routes: string[];
  minutes?: number;
  bodyHtml: string;
  related?: string[];
};

export const HELP_CATEGORIES = [
  { key: "start", label: "צעדים ראשונים", icon: "sparkle", sub: "העיצוב החדש, חיפוש וקיצורים" },
  { key: "customers", label: "מכירות", icon: "users", sub: "לקוחות, כרטיס לקוח, מנויים וגבייה" },
  { key: "finance", label: "כספים", icon: "wallet", sub: "הכנסות, הוצאות, בנק ומסמכים" },
  { key: "office", label: "משרד", icon: "task", sub: "משימות, ישיבות, טפסים ואוטומציות" },
  { key: "settings", label: "הגדרות", icon: "gear", sub: "הרשאות, משתמשים וחיבורים" },
];

const ul = (items: string[]) => `<ul>${items.map((i) => `<li>${i}</li>`).join("")}</ul>`;
const ol = (items: string[]) => `<ol>${items.map((i) => `<li>${i}</li>`).join("")}</ol>`;

export const HELP_ARTICLES: HelpArticle[] = [
  {
    slug: "new-design",
    title: "הכירו את העיצוב החדש",
    summary: "איפה נמצא כל דבר: המודולים, הסרגל, הסרגל העליון, החיפוש והתפריט האישי",
    category: "start",
    keywords: ["עיצוב", "סרגל", "תפריט", "מבנה", "מודולים", "מכירות", "כספים"],
    routes: ["/"],
    minutes: 2,
    bodyHtml:
      "<p>אותה מערכת ואותם נתונים — בפריסה חדשה, נקייה ומהירה יותר.</p>" +
      "<h3>מודולים — מכירות וכספים</h3>" +
      ul([
        "בראש המסך בוחרים מודול: <b>מכירות</b> — לקוחות, מנויים וגבייה; <b>כספים</b> — הכנסות והוצאות, התאמות בנק, ספקים, דרישות תשלום, קבלות ומסמכים, פרויקטים.",
        "הסרגל בצד מציג את המסכים של המודול הפעיל, וכל מודול בצבע משלו.",
        "פתיחת מסך (מהחיפוש, מקישור או מכרטיס) מעבירה אוטומטית למודול שלו.",
      ]) +
      "<h3>הסרגל (ימין)</h3>" +
      ul([
        "המסכים של המודול מקובצים לפי נושא; <b>משרד</b> (משימות, ישיבות, טפסים, אוטומציות) קבוע בכל המודולים.",
        "כל ההגדרות מרוכזות במקום אחד — <b>הגדרות</b> בתחתית הסרגל.",
        "בתחתית: התפריט האישי (אזור אישי, מצב תצוגה, החלפת ארגון, התנתקות) ומרכז ההודעות.",
      ]) +
      "<h3>הסרגל העליון</h3>" +
      ul([
        "<b>חיפוש</b> (Ctrl+K) — לקוחות, משתמשים, משימות, טפסים, מסכים, הגדרות ומדריכים.",
        "<b>חדש</b> — יצירה מהירה (לקוח, משימה, הכנסה, הוצאה…) מכל מסך. אפשר להצמיד עד 4 קיצורים.",
        "<b>הגדרות קשורות</b> (Ctrl+,) — ההגדרות שמשפיעות על המסך שבו אתם נמצאים.",
        "<b>אוטומציות</b> — מה קורה לבד במסך הזה, והוספת אוטומציה בלחיצה.",
        "<b>היום</b> — מה נכנס, מה באיחור ומה המשימות שלכם להיום.",
      ]),
    related: ["search", "related-settings", "today"],
  },
  {
    slug: "search",
    title: "חיפוש בכל המערכת — Ctrl+K",
    summary: "מוצאים לקוח, משתמש, משימה, טופס, מסך או הגדרה מכל מקום",
    category: "start",
    keywords: ["חיפוש", "קיצור", "ctrl k", "פקודות"],
    routes: [],
    minutes: 1,
    bodyHtml:
      "<p>לחצו על <b>חיפוש</b> בסרגל העליון או על <kbd>Ctrl</kbd>+<kbd>K</kbd> מכל מסך.</p>" +
      ul([
        "מקלידים חלק משם, טלפון, אימייל או קוד — התוצאות מופיעות מקובצות: לקוחות, משתמשים, משימות, טפסים.",
        "גם <b>מסכים</b>, <b>פעולות</b> (״לקוח חדש״), <b>הגדרות</b> ו<b>מדריכים</b> נמצאים בחיפוש.",
        "חיצים למעלה/למטה לבחירה, Enter לפתיחה, Esc לסגירה.",
      ]),
  },
  {
    slug: "related-settings",
    title: "הגדרות קשורות למסך",
    summary: "כל ההגדרות שמשפיעות על המסך הנוכחי — בלחיצה אחת, בלי לחפש",
    category: "start",
    keywords: ["הגדרות", "קשורות", "ctrl ,"],
    routes: [],
    minutes: 1,
    bodyHtml:
      "<p>הכפתור <b>הגדרות קשורות</b> בסרגל העליון (או <kbd>Ctrl</kbd>+<kbd>,</kbd>) פותח פאנל עם ההגדרות שמשפיעות על המסך שבו אתם נמצאים — וליד כל אחת הסבר למה היא רלוונטית כאן.</p>" +
      ul([
        "הגדרות שמסומנות <b>כאן</b> נערכות בתוך הפאנל, בלי לעזוב את המסך.",
        "הגדרה שנמצאת במסך אחר — מעבר אליה, ובראש המסך מופיע כפתור <b>חזרה</b> למסך שממנו הגעתם.",
        "בחיפוש למעלה אפשר למצוא כל הגדרה במערכת.",
      ]),
  },
  {
    slug: "today",
    title: "היום בתוכנה",
    summary: "מבט אחד על היום: תשלומים, חובות, משימות, ישיבות והגשות טפסים",
    category: "start",
    keywords: ["היום", "סיכום", "משימות היום"],
    routes: [],
    minutes: 1,
    bodyHtml:
      "<p>הכפתור <b>היום</b> בפינה העליונה פותח את סיכום היום של הארגון:</p>" +
      ul([
        "כמה נכנס היום ומה צפוי השבוע.",
        "מה דורש החלטה: חובות פתוחים, הרשאות שעומדות לפוג, הגשות טפסים שלא טופלו.",
        "המשימות שלכם להיום — אפשר לסמן כבוצע ישירות מהפאנל.",
        "ישיבות צוות קרובות.",
      ]) +
      "<p>אפשר לבחור שהפאנל ייפתח לבד בכניסה הראשונה בכל יום.</p>",
  },
  {
    slug: "ai-assistant",
    title: "עוזר AI",
    summary: "שואלים בשפה חופשית על לקוחות, גבייה, משימות והיום — מקבלים טבלאות, והעוזר מכין פעולות לאישורכם",
    category: "start",
    keywords: ["AI", "עוזר", "בינה מלאכותית", "צ'אט", "Ctrl+J", "דוח", "טבלה"],
    routes: [],
    minutes: 3,
    bodyHtml:
      "<p>הכפתור <b>AI</b> בסרגל העליון (או Ctrl+J) פותח את העוזר. אפשר גם לכתוב שאלה בחיפוש (Ctrl+K) ולבחור «לשאול את עוזר ה-AI».</p>" +
      "<h3>מה אפשר לשאול</h3>" +
      ul([
        "«מה מחכה לי היום?» — סיכום תשלומים, חובות, משימות וישיבות.",
        "«מי הלקוחות עם החוב הגבוה ביותר?», «אילו הרשאות פגות החודש?», «מה נכנס לקופה של כהן?» — מקבלים טבלה מתחת לתשובה.",
        "«איך מוסיפים קטגוריה?» — העוזר עונה לפי המדריכים של המערכת ויכול לפתוח את המדריך.",
        "«תבדוק אם יש בעיות בנתונים» — סיכום בדיקת הנתונים, עם מעבר למסך התיקון.",
      ]) +
      "<h3>מה העוזר מכין לאישורכם</h3>" +
      ul([
        "משימה, לקוח חדש, עדכון פרטים בכרטיס לקוח (לפני ← אחרי), הכנסה לקופה, תשלום מלקוח, מייל ללקוח ואוטומציה שלמה.",
        "כל פעולה מגיעה ככרטיס — שום דבר לא נכתב עד שלוחצים «אישור». בזמן האישור נבדקות שוב ההרשאות שלכם.",
        "כשמעדיפים למלא לבד — העוזר פותח את הטופס הרגיל, את ההגדרה או את המסך המתאים בלחיצה.",
      ]) +
      "<h3>טבלאות ודוחות</h3>" +
      ul([
        "כל טבלה אפשר להרחיב לתצוגה מלאה ולייצא ל-CSV (נפתח באקסל בעברית).",
        "📌 שומר את הטבלה כ<b>דוח שמור</b> (הכפתור 📊 בראש הפאנל) — הרצה חוזרת מביאה את הנתונים העדכניים, בהרשאות שלכם באותו רגע.",
        "👍 / 👎 מתחת לכל תשובה עוזרים לנו לשפר; מנהל הארגון רואה את המשובים בהגדרות.",
      ]) +
      "<h3>פרטיות והרשאות</h3>" +
      ul([
        "העוזר רואה רק את מה שמותר לכם לראות בארגון הפעיל — אותן הרשאות ואותה מדיניות שדות כמו במסכים.",
        "השיחות שלכם שמורות ונגישות רק לכם (שיחות קודמות בראש הפאנל).",
        "השימוש נמדד בקרדיטים של הארגון — היתרה מוצגת בראש הפאנל.",
      ]),
    related: ["ai-credits", "ai-data-entry", "data-audit", "ai-automations"],
  },
  {
    slug: "ai-data-entry",
    title: "הזנה חכמה",
    summary: "מעלים Excel או CSV — והסוכן הופך אותו לתשלומים של לקוחות, הכנסות, הוצאות לספקים ומכירות, או לרשימת לקוחות / ספקים, שורה-שורה באישורכם",
    category: "finance",
    keywords: ["הזנה", "ייבוא", "אקסל", "excel", "csv", "דף בנק", "קובץ", "AI", "סוכן", "Alt+6", "מטבע", "תשלומים", "פיצול", "רשימת לקוחות", "ספקים", "העלאה קבועה", "התאמה בנקאית"],
    routes: ["/ai/data-entry"],
    minutes: 4,
    bodyHtml:
      "<p><b>Alt+6</b> מכל מסך (או <b>משרד ← הזנה חכמה</b>). מעלים קובץ Excel או CSV — דף בנק, דוח תנועות, רשימת תשלומים או מכירות.</p>" +
      ol([
        "«מה בקובץ»: זיהוי אוטומטי, דף בנק, תשלומים, הכנסות, הוצאות או מכירות. «עמודות»: מה בכל עמודה — ה-AI עוזר רק בעמודות שלא ברורות. בצד בוחרים קופה (להכנסות והוצאות), קטגוריה ואמצעי תשלום לכל הקובץ.",
        "כל שורה מקבלת סוג — תשלום מלקוח (Alt+1), הכנסה (Alt+2), הוצאה לספק (Alt+3), מכירה חדשה (Alt+4) או דילוג (Alt+0) — ושיוך עם עד 4 מועמדים (מקשים 1–4) וסיבת ההתאמה. אפשר לחפש, או להוסיף לקוח / ספק חדש מהכרטיס.",
        "Enter — אישור והבאה, Shift+Enter — חזרה. השורות המאושרות נרשמות ברקע. ב«כל השורות» אפשר לאשר כמה יחד, וה-KPI למעלה הם מסננים.",
        "תיקון בשורה אחת (סוג / קטגוריה / קרן) — הסוכן מציע להחיל אותו על השורות הדומות ולזכור אותו ככלל לקבצים הבאים.",
        "«שיחה עם הסוכן»: «כל השורות של ביט — תשלומים», «למה שורה 12 היא הוצאה?» — הוא משנה רק את השורות על המסך. «ניתוח הקובץ»: עובדות, אזהרות (לחיצה מסננת את השורות שלהן) והמלצות.",
      ]) +
      ul([
        "כל שורה נשמרת דרך המסכים הרגילים ובהרשאות שלכם. תשלום של לקוח עם עסקה פתוחה נרשם כתשלום על העסקה (אפשר לשנות).",
        "שורה שכבר קיימת במערכת (אותה אסמכתא וסכום) או דומה לרשומה קיימת — לא נרשמת עד שתחליטו.",
        "אפשר לבטל כל רישום מהכרטיס. הקבצים נשמרים ב«קבצים אחרונים» — אפשר לחזור ולהמשיך.",
      ]) +
      "<h3>בכרטיס השורה</h3>" +
      ul([
        "<b>מטבע ושער:</b> סכום בדולר / יורו / לירה (מעמודה בקובץ או מסימן בסכום) נרשם במטבע ובסכום המקוריים; השער נשמר בהערות. מכירה נשמרת במטבע שלה עם השער. תשלום על עסקה במטבע זר צריך שער — הוא נרשם בשקלים.",
        "<b>מספר תשלומים:</b> מעמודה בקובץ או «6 תשלומים» בהערות. במכירה נשמר בעסקה (עד 60); בתשלום ובהכנסה — בהערות הרשומה.",
        "<b>פיצול לקטגוריות</b> (הכנסה / הוצאה): «פיצול לשתי קטגוריות» — כל חלק נרשם כרשומה נפרדת עם אותה אסמכתא, והסכומים חייבים להסתכם לסכום השורה. ביטול השורה מבטל את כל החלקים.",
      ]) +
      "<h3>דף בנק</h3>" +
      ul([
        "בוחרים «דף בנק» וקופה — «בדיקת הקובץ מול דף הבנק» מראה כמה שורות חדשות וכמה כבר בבנק, ו«טעינה» מכניסה רק את החדשות למסך ההתאמה הבנקאית של הקופה.",
        "בסריקה כל הכנסה והוצאה מקבלת את שורת הבנק הפתוחה עם אותו סכום (עד 3 ימים) — ובאישור הרשומה מותאמת אליה. אפשר לבטל את ההתאמה בכרטיס, וביטול הרישום מחזיר את שורת הבנק ל«לא מותאם».",
        "צריך הרשאת ניהול התאמות בנקים ומודול התאמה בנקאית פעיל; בלי זה התנועות נרשמות בלי התאמה.",
      ]) +
      "<h3>רשימת לקוחות או ספקים</h3>" +
      ul([
        "קובץ בלי סכומים, עם שמות וטלפונים — הסוכן מציע «רשימת לקוחות» (או בוחרים «רשימת ספקים» לקופה). כל שורה נבדקת מול המערכת: טלפון, ת.ז, מייל, קוד ושם זהה — וגם שורה כפולה בתוך הקובץ.",
        "לקוח שכבר קיים: «לא לגעת», «השלמת שדות ריקים» (רק מה שחסר אצלו) או «עדכון הרשומה» — רואים בדיוק מה ישתנה. השם לא משתנה מרשימה. ספק קיים — רק מדלגים.",
        "Enter מאשר ורושם, «אישור כל המוכנות» רושם את כל החדשות. ערך שגוי (טלפון / מייל) מתקנים בצד ובודקים מחדש. ביטול: לקוח שנוצר עובר לארכיון, לקוח שעודכן מקבל בחזרה את הערכים הקודמים, ספק שנוצר עובר לארכיון.",
      ]) +
      "<h3>העלאה קבועה</h3>" +
      ul([
        "קובץ שמגיע כל חודש (דף בנק, דוח אשראי): בתפריט «עוד» ← «העלאה קבועה…» — שם, תדירות ומועד הבא. הסוכן זוכר את מבנה הקובץ ואת הבחירות (סוג, עמודות, קופה, ברירות מחדל וכללים).",
        "בפעם הבאה שמעלים קובץ עם אותן עמודות (גם בסדר אחר) — הוא מזוהה, ההגדרות מוחלות לבד והמועד הבא מתעדכן. במסך הפתיחה רואים את ההעלאות הקבועות ומה באיחור.",
        "באשפי הייבוא של הקופה (הכנסות, הוצאות, שורות בנק) יש באנר «מומלץ — הזנה חכמה» שפותח את הסוכן עם הסוג המתאים.",
      ]),
    related: ["ai-assistant", "income-expenses"],
  },
  {
    slug: "data-audit",
    title: "בדיקת נתונים",
    summary: "הסוכן עובר על הנתונים ומוצא פערים — «שולם» שלא תואם לתשלומים, עסקאות כפולות, לידים לא מקושרים, כפילויות לקוחות, יתרות — ומתקן באישור, עם ביטול",
    category: "settings",
    keywords: ["בדיקה", "נתונים", "כפילויות", "תיקון", "איכות", "AI", "סוכן", "עסקאות", "לידים"],
    routes: ["/ai/data-audit"],
    minutes: 2,
    bodyHtml:
      "<p><b>משרד ← בדיקת נתונים</b> (או <b>חדש ← בדיקת נתונים (AI)</b>) פותחת את כרטיס הסוכן: הוא מריץ את הבדיקות על הנתונים של הארגון ומציג מה נמצא, לפי סדר ההשפעה — קודם כסף שמוצג לא נכון.</p>" +
      ul([
        "מכירות (כשהמודול פעיל): «שולם» שלא תואם לתשלומים, סה״כ עסקה שלא תואם למחיר פחות ההנחה, עסקאות ותשלומים כפולים, עסקאות נציג בלי נציג או בלי כלל עמלה, לידים עם טלפון של לקוח קיים, עסקאות בלי ליד, לידים כפולים, שלבי ליד שגויים, שיחות באיחור בלי נציג, צורות תשלום ושמות מוצרים כטקסט.",
        "לקוחות בלי טלפון או מייל, כפילויות (אותו טלפון / אותו שם ועיר), טלפונים ומיילים שגויים, לקוחות בלי עיר.",
        "מנויים פעילים עם הרשאה שפגה או של לקוח שבוטל, יתרות שלא תואמות לתנועות, הכנסות בלי קטגוריה, משימות באיחור בלי אחראי ודרישות תשלום תקועות.",
      ]) +
      "<h3>תיקון</h3>" +
      ul([
        "בכל בדיקה הסוכן מסמן מה מומלץ לתקן — כפתור אחד «תיקון N מומלצות». ב«פירוט» אפשר לבחור רשומות אחרות; שורה עם אזהרה (למשל «שם אחר באותו טלפון») לא מסומנת מראש.",
        "לפני כל תיקון הבדיקה רצה שוב — רק רשומות שעדיין מתאימות ישתנו, דרך המסכים הרגילים ובהרשאות שלכם. כפילויות מבוטלות, לא נמחקות.",
        "כל תיקון נשמר ב«מה תוקן» ואפשר לבטל אותו. רשומה ששונתה מאז התיקון לא תשוחזר (כדי לא לדרוס עבודה).",
        "«שיחה עם הסוכן»: מתארים בעיה במילים («הגבייה לא מסתדרת») — הסוכן בודק, מסביר מה פער אמיתי ומה מכוון, ומציע כפתורי תיקון. שום דבר לא משתנה בלי אישורכם.",
      ]),
    related: ["ai-assistant"],
  },
  {
    slug: "ai-automations",
    title: "יצירת אוטומציה עם AI",
    summary: "כותבים במילים פשוטות מה צריך לקרות לבד — ומעצב האוטומציות מכין טיוטה מדויקת לאישור",
    category: "office",
    keywords: ["אוטומציה", "AI", "מעצב", "תזמון", "מייל אוטומטי"],
    routes: ["/automations"],
    minutes: 2,
    bodyHtml:
      ol([
        "במסך «אוטומציות» או בחלון «אוטומציות בדף זה» בוחרים בלשונית «יצירה עם AI» וכותבים מה צריך לקרות — מתי, למי ומה לשלוח. אפשר גם לבקש מעוזר ה-AI.",
        "מעצב האוטומציות בודק בנתונים (למשל כמה לקוחות עם חוב יש היום) ומכין טיוטה לפי מה שהמערכת יודעת לעשות: מייל, משימה או דוח — לפי לוח זמנים או כשמשהו קורה.",
        "אם חסר פרט — הוא שואל; אם כבר יש אוטומציה או תבנית דומה — הוא מציע אותה במקום כפילות.",
        "«הפעלה» יוצרת את האוטומציה; «עריכה לפני הפעלה» פותחת אותה בעורך הרגיל.",
      ]) +
      ul(["לא מתזמנים בשבת, וההודעות נשלחות במייל בלבד.", "אוטומציה שנוצרה עם AI מסומנת כך, עם הבקשה המקורית."]),
    related: ["automations", "ai-assistant"],
  },
  {
    slug: "ai-credits",
    title: "קרדיטים של AI",
    summary: "כל שימוש ב-AI נמדד בקרדיטים מהארנק של הארגון — הקצאה חודשית, רכישה, ומי רשאי להשתמש",
    category: "settings",
    keywords: ["קרדיטים", "AI", "יתרה", "מגבלה", "רכישה", "עלות"],
    routes: [],
    minutes: 1,
    bodyHtml:
      ul([
        "כל שימוש בסוכני ה-AI (העוזר, מעצב האוטומציות, הזנה חכמה ובדיקת נתונים) נמדד בקרדיטים ומחויב מהארנק של הארגון.",
        "לארגון יש הקצאה חודשית שמתאפסת בתחילת כל חודש, וקרדיטים שנרכשו — שלא מתאפסים ונכנסים לשימוש רק אחרי שההקצאה נגמרה.",
        "בהגדרות ← «עוזר AI — קרדיטים והרשאות» קובעים אם כולם רשאים או רק משתמשים מסומנים, ומגבלה אישית חודשית לכל משתמש.",
        "כשהיתרה נגמרת או שמשתמש הגיע למגבלה שלו — ה-AI מציג הודעה ולא ממשיך עד חידוש היתרה. מנהלי הקרדיטים אינם מוגבלים במגבלה אישית.",
        "צריך עוד קרדיטים? «בקשת רכישה» — והקרדיטים נוספים ליתרה מיד אחרי אישור מנהלי המערכת.",
      ]),
    related: ["ai-assistant"],
  },
  {
    slug: "recycle-bin",
    title: "סל מחזור",
    summary: "מחקתם בטעות? משימות, אוטומציות וטפסים נשמרים 30 יום",
    category: "settings",
    keywords: ["מחיקה", "שחזור", "נמחק", "סל"],
    routes: [],
    minutes: 1,
    bodyHtml:
      "<p>הגדרות ← <b>סל מחזור</b> מציג את מה שנמחק בארגון ב־30 הימים האחרונים — משימות, אוטומציות, טפסים וקליטות טפסים.</p>" +
      ul([
        "<b>שחזור</b> מחזיר את הפריט בדיוק כמו שהיה.",
        "אוטומציה חוזרת <b>מושהית</b>, וקליטת טפסים חוזרת <b>כבויה</b> — כדי שלא ירוצו לבד לפני שבדקתם.",
        "רואים ומשחזרים רק פריטים שמותר לכם למחוק.",
        "אחרי 30 יום הפריט יוצא מהסל. אפשר גם להסיר ממנו מיד.",
        "קטגוריות וקופות לא נכנסות לסל: במחיקה שלהן התנועות מועברות לקטגוריה/קופה אחרת.",
      ]),
  },
  {
    slug: "dashboard",
    title: "תמונת מצב",
    summary: "המדדים המרכזיים של הארגון והדוחות לפי תקופה",
    category: "start",
    keywords: ["תמונת מצב", "דשבורד", "לוח בקרה", "מדדים"],
    routes: ["/"],
    minutes: 2,
    bodyHtml:
      "<p>תמונת המצב מרכזת את המדדים החשובים: מנויים פעילים, חובות, תשלומים החודש ומשימות פתוחות.</p>" +
      ul([
        "לחיצה על מדד פותחת את הפירוט שמאחוריו.",
        "בדוחות לפי תקופה בוחרים טווח תאריכים ומקבלים סיכום לפי קטגוריה.",
        "רוצים לקבל את תמונת המצב במייל כל שבוע? הוסיפו אוטומציה „דוח שבועי” מכפתור האוטומציות.",
      ]),
    related: ["automations"],
  },
  {
    slug: "customers",
    title: "רשימת הלקוחות",
    summary: "חיפוש, סינון, תצוגות שמורות, ייבוא וייצוא",
    category: "customers",
    keywords: ["לקוחות", "אלפון", "קופות", "רשימה", "ייבוא"],
    routes: ["/customers"],
    minutes: 3,
    bodyHtml:
      "<p>הרשימה מציגה את כל הלקוחות (או הקופות) של הארגון.</p>" +
      ul([
        "<b>חיפוש</b> מעל הטבלה — לפי שם, קוד, טלפון או אימייל.",
        "<b>סינון</b> מכל כותרת עמודה; כרטיסי הסיכום מעל הטבלה מסננים בלחיצה.",
        "<b>עמודות ותצוגות</b> — בוחרים עמודות, שומרים תצוגה ומחליפים ביניהן.",
        "<b>ייבוא</b> מאקסל/CSV עם מיפוי עמודות וזיהוי כפילויות; <b>ייצוא</b> לאקסל.",
        "לחיצה על שורה פותחת את <b>כרטיס הלקוח</b>.",
      ]),
    related: ["customer-card", "subscriptions"],
  },
  {
    slug: "customer-card",
    title: "כרטיס לקוח",
    summary: "פרטים, אנשי קשר, כספים, עובר ושב, מנויים, חובות ופעילות",
    category: "customers",
    keywords: ["כרטיס", "לקוח", "חוב", "עובר ושב", "מנוי"],
    routes: ["/customers"],
    minutes: 3,
    bodyHtml:
      "<p>הכרטיס נפתח כחלון צד, כך שהרשימה נשארת לידכם.</p>" +
      ul([
        "<b>פרטים</b> — עריכה ישירה של השדות (לפי מדיניות השדות של התפקיד שלכם).",
        "<b>כספים</b> — מנויים, חובות, תשלומים ועובר ושב של הלקוח.",
        "<b>פעילות</b> — כל מה שקרה בכרטיס, כולל מיילים שנשלחו.",
        "חיצים בראש הכרטיס עוברים ללקוח הקודם/הבא ברשימה.",
      ]),
    related: ["customers", "subscriptions"],
  },
  {
    slug: "subscriptions",
    title: "מנויים ותשלומים",
    summary: "טבלת המנויים לפי חודש, סטטוס תשלום, הו״ק והרשאות",
    category: "customers",
    keywords: ["מנויים", "תשלומים", "הוראת קבע", "הו״ק", "הרשאה"],
    routes: ["/subscriptions"],
    minutes: 3,
    bodyHtml:
      "<p>כל מנוי בשורה, וכל חודש בעמודה — רואים מיד מי שילם, מי בחוב ומי בהוראת קבע.</p>" +
      ul([
        "צבעים: ירוק = שולם, כתום = חלקי/בבדיקה, אדום = חוב.",
        "לחיצה על תא פותחת את פרטי התשלום ומאפשרת לרשום תשלום ידני.",
        "תשלומים מנדרים פלוס נקלטים לבד (ראו <b>הגדרות קשורות</b> ← נדרים פלוס).",
      ]),
    related: ["nedarim"],
  },
  {
    slug: "income-expenses",
    title: "הכנסות והוצאות",
    summary: "רישום תנועות, קטגוריות, קופות, ייבוא קבצים וסיכומים",
    category: "finance",
    keywords: ["הכנסה", "הוצאה", "תנועות", "קופה", "ייבוא"],
    routes: ["/income-expenses"],
    minutes: 3,
    bodyHtml:
      ul([
        "<b>חדש ← הכנסה / הוצאה</b> (או Alt+3 / Alt+4) — רישום מהיר מכל מסך.",
        "כל תנועה משויכת לקטגוריה ולקופה פנימית — המבנה מוגדר ב<b>פרויקטים</b>.",
        "ייבוא קובץ בנק / אקסל יוצר תנועות לפי מיפוי.",
        "סינון לפי לקוח בראש המסך מציג רק את התנועות שלו.",
      ]),
    related: ["bank", "structure"],
  },
  {
    slug: "bank",
    title: "התאמות בנק",
    summary: "התאמת תנועות הבנק לתנועות במערכת",
    category: "finance",
    keywords: ["בנק", "התאמה", "דף בנק"],
    routes: ["/income-expenses/bank"],
    bodyHtml:
      ol([
        "מעלים דף בנק (אקסל/CSV).",
        "המערכת מציעה התאמה לכל שורה לפי סכום ותאריך.",
        "מאשרים התאמה, או יוצרים תנועה חדשה משורה שלא נמצאה לה התאמה.",
      ]),
  },
  {
    slug: "suppliers",
    title: "ספקים ודרישות תשלום",
    summary: "ניהול ספקים, דרישות תשלום ואישורן",
    category: "finance",
    keywords: ["ספק", "דרישת תשלום", "אישור"],
    routes: ["/income-expenses/suppliers", "/income-expenses/payment-requests"],
    bodyHtml:
      ul([
        "לכל ספק — פרטים, הוצאות ומסמכים.",
        "דרישת תשלום עוברת אישור ואז נרשמת כהוצאה.",
        "אוטומציה יכולה להזכיר על דרישות פתוחות.",
      ]),
  },
  {
    slug: "receipts",
    title: "קבלות ומסמכי הנה״ח",
    summary: "הנפקת קבלות ומסמכים דרך EZcount",
    category: "finance",
    keywords: ["קבלה", "חשבונית", "EZcount", "מסמך"],
    routes: ["/income-expenses/receipts", "/income-expenses/accounting", "/income-expenses/documents"],
    bodyHtml:
      ul([
        "קבלה מופקת מתוך הכנסה או תשלום, ונשמרת כאן.",
        "החיבור ל-EZcount מוגדר ב<b>הגדרות ← EZcount</b>.",
        "מסמכים כלליים (חוזים, אישורים) נשמרים בלשונית מסמכים.",
      ]),
  },
  {
    slug: "structure",
    title: "פרויקטים — קטגוריות, קופות ותקציבים",
    summary: "המבנה הכספי של הארגון ומיפויי נדרים וקמפיינים",
    category: "finance",
    keywords: ["קטגוריה", "קופה", "תקציב", "מיפוי", "פרויקט"],
    routes: ["/structure"],
    minutes: 2,
    bodyHtml:
      ul([
        "<b>קטגוריות</b> — סוגי הכנסה והוצאה, כולל סכום ומחזוריות ברירת מחדל.",
        "<b>קופות</b> — הקופות הפנימיות שאליהן נרשמות התנועות.",
        "<b>תקציבים</b> — תקציב לכל קטגוריה ומעקב ניצול.",
        "<b>מיפוי נדרים / קמפיינים</b> — לאן נכנסת כל עסקה שמגיעה מבחוץ.",
      ]),
  },
  {
    slug: "tasks",
    title: "משימות",
    summary: "משימות אישיות וכלליות, שיוך, תאריך יעד וסטטוס",
    category: "office",
    keywords: ["משימה", "תזכורת", "שיוך", "באיחור"],
    routes: ["/tasks"],
    bodyHtml:
      ul([
        "<b>חדש ← משימה</b> מכל מסך.",
        "שיוך לאיש צוות, תאריך יעד ועדיפות.",
        "משימות באיחור מסומנות באדום ומופיעות ב<b>היום</b>.",
        "אוטומציה יכולה ליצור משימות לבד (למשל: חוב פתוח מעל חודש).",
      ]),
    related: ["automations", "today"],
  },
  {
    slug: "meetings",
    title: "ישיבות צוות",
    summary: "הכנה, סדר יום, סיכום, משימות המשך וארכיון",
    category: "office",
    keywords: ["ישיבה", "סדר יום", "סיכום", "פרוטוקול"],
    routes: ["/meetings"],
    bodyHtml:
      ol([
        "<b>הכנה</b> — משתתפים ונושאים לסדר היום.",
        "<b>בישיבה</b> — רישום החלטות ומשימות המשך לכל נושא.",
        "<b>סיכום</b> — הדפסה או שליחה, ומשימות ההמשך נכנסות לרשימת המשימות.",
      ]),
  },
  {
    slug: "forms",
    title: "טפסים וקליטה",
    summary: "טפסים ציבוריים, שיוך שדות ומה נוצר מכל הגשה",
    category: "office",
    keywords: ["טופס", "הגשה", "קליטה", "קישור"],
    routes: ["/forms"],
    bodyHtml:
      ul([
        "בונים טופס, משייכים כל שדה לשדה בכרטיס, ומקבלים קישור ציבורי.",
        "כל הגשה נכנסת לרשימה — אפשר לקלוט אותה ככרטיס חדש או לעדכן כרטיס קיים.",
        "אוטומציה יכולה לשלוח מייל או ליצור משימה על כל הגשה.",
      ]),
    related: ["automations"],
  },
  {
    slug: "automations",
    title: "אוטומציות",
    summary: "מיילים, משימות ודוחות שקורים לבד — לפי לוח זמנים או אירוע",
    category: "office",
    keywords: ["אוטומציה", "תזמון", "מייל", "דוח", "טריגר"],
    routes: ["/automations"],
    minutes: 3,
    bodyHtml:
      "<p>כפתור <b>אוטומציות</b> בסרגל העליון מציג מה קורה לבד במסך הזה, ומציע תבניות מוכנות.</p>" +
      "<h3>שלושה חלקים לכל אוטומציה</h3>" +
      ol([
        "<b>מתי</b> — לוח זמנים (כל יום / שבוע / חודש) או אירוע (לקוח חדש, תשלום, הגשת טופס, משימה באיחור).",
        "<b>תנאים</b> — אופציונלי: רק לקוחות עם חוב, רק סכום מעל…",
        "<b>מה לעשות</b> — שליחת מייל, יצירת משימה או שליחת דוח.",
      ]) +
      ul([
        "אפשר להשהות, להריץ עכשיו לבדיקה ולראות היסטוריית הרצות.",
        "כל מייל שנשלח מופיע ב<b>יומן דואר</b>.",
      ]),
    related: ["mail-log", "mailing"],
  },
  {
    slug: "mailing",
    title: "דיוור",
    summary: "מייל אחד לקהל שלם — לקוחות מסוננים או מנויים, עם הסרה בלחיצה ודוח פתיחות",
    category: "office",
    keywords: ["דיוור", "ניוזלטר", "רשימת תפוצה", "מייל המוני", "הסרה", "מנויים", "טופס הרשמה", "פרסומת"],
    routes: ["/mailing"],
    minutes: 4,
    bodyHtml:
      "<p><b>דיוור חדש</b> עובר שלושה שלבים:</p>" +
      ol([
        "<b>תוכן</b> — נושא, ועורך שבונים בו את המייל מרכיבים: כותרת, טקסט, כפתור, תמונה. <b>שדה אישי</b> מכניס שם של כל נמען (למשל {{first_name|ידיד יקר}}). התצוגה המקדימה היא בדיוק מה שיישלח.",
        "<b>קהל</b> — לקוחות (אותם סינונים של רשימת הלקוחות), מנויים או רשימה מודבקת. רואים מיד כמה יקבלו, וכמה ירדו כי הן כפולות, שגויות או חסומות.",
        "<b>שליחה</b> — מייל בדיקה לעצמכם, בדיקת A/B על הנושא (אופציונלי), ושליחה עכשיו או בתזמון.",
      ]) +
      "<h3>מה קורה לבד</h3>" +
      ul([
        "בתחתית כל מייל יש קישור הסרה ושם וכתובת הארגון. מי שמסיר את עצמו לא יקבל יותר דיוור — גם לא מאוטומציות.",
        "<b>דבר פרסומת</b> (כולל בקשת תרומה) מקבל \"פרסומת:\" בתחילת הנושא, כפי שהחוק דורש. לכן חובה למלא כתובת ארגון בהגדרות הדיוור.",
        "כתובת שלא קיימת או שדיווחה על ספאם נחסמת אוטומטית.",
        "בדוח של כל דיוור: כמה נפתחו, על מה הקליקו, מי הסיר את עצמו. אפשר להשהות, להמשיך או לעצור באמצע.",
      ]) +
      "<h3>מנויים וטפסי הרשמה</h3>" +
      ul([
        "<b>טופס הרשמה</b> נותן קישור ו־קוד הטמעה לאתר. עם <b>אישור כפול</b> הנרשם מאשר במייל לפני שהוא נכנס לרשימה.",
        "אפשר להוסיף לכל טופס <b>מייל ברוכים הבאים</b> שנשלח אוטומטית אחרי האישור.",
        "בלשונית <b>נמענים</b>: רשימת המנויים והחסומים. החזרה של מי שהסיר את עצמו — רק אם ביקש במפורש.",
      ]) +
      "<p>יש מכסה חודשית של מיילים; ההתקדמות מופיעה בסקירה. מהרשימה של הלקוחות: ⋯ ← <b>דיוור ללקוחות</b>.</p>",
    related: ["automations", "mail-log"],
  },
  {
    slug: "mail-log",
    title: "יומן דואר",
    summary: "כל מייל שנשלח מהמערכת והסטטוס שלו",
    category: "office",
    keywords: ["מייל", "דיוור", "נשלח", "נכשל"],
    routes: ["/mail-log"],
    bodyHtml: ul(["כל מייל — נמען, נושא, מקור (אוטומציה, טופס, ידני) וסטטוס.", "מייל שנכשל אפשר לשלוח שוב."]),
  },
  {
    slug: "guidance",
    title: "מסמך הנחיות",
    summary: "נהלי העבודה של הארגון, עם גרסאות",
    category: "office",
    keywords: ["הנחיות", "נוהל", "גרסה"],
    routes: ["/org-guidance"],
    bodyHtml: ul(["עורכים את המסמך ושומרים גרסה.", "כל גרסה נשמרת — אפשר לחזור לגרסה קודמת."]),
  },
  {
    slug: "users",
    title: "משתמשים ותפקידים",
    summary: "הזמנת משתמשים, תפקיד בארגון וסטטוס חברות",
    category: "settings",
    keywords: ["משתמש", "הזמנה", "תפקיד", "צוות"],
    routes: ["/users"],
    bodyHtml:
      ul([
        "<b>חדש ← משתמש</b> — הזמנה במייל עם תפקיד.",
        "התפקיד קובע מה המשתמש רואה ועושה (הרשאות ותבניות תפקיד).",
        "השהיית משתמש חוסמת כניסה בלי למחוק את ההיסטוריה שלו.",
      ]),
    related: ["permissions"],
  },
  {
    slug: "permissions",
    title: "הרשאות ותבניות תפקיד",
    summary: "מה כל תפקיד רואה ועושה — לפי מסך ופעולה",
    category: "settings",
    keywords: ["הרשאה", "תפקיד", "תבנית", "גישה"],
    routes: ["/settings/permissions", "/settings"],
    bodyHtml:
      ul([
        "לכל תפקיד תבנית הרשאות — צפייה, עריכה ומחיקה לפי מסך.",
        "אפשר להתאים הרשאות למשתמש מסוים מעבר לתבנית.",
        "מדיניות שדות (בהגדרות) מסתירה או נועלת שדות לפי תפקיד.",
      ]),
  },
  {
    slug: "nedarim",
    title: "חיבור נדרים פלוס",
    summary: "מפתח API, Webhooks, כתובות IP ושליפת עסקאות",
    category: "settings",
    keywords: ["נדרים", "webhook", "סנכרון", "api"],
    routes: ["/settings"],
    bodyHtml:
      ol([
        "בהגדרות ← נדרים פלוס מזינים את מזהה המוסד ומפתח ה-API.",
        "מעתיקים את כתובת ה-Webhook ומגדירים אותה בנדרים.",
        "ממפים כל סוג עסקה לקטגוריה וקופה ב<b>פרויקטים ← מיפוי נדרים</b>.",
      ]),
  },
  {
    slug: "personal",
    title: "אזור אישי",
    summary: "פרטים אישיים, סיסמה ומראה",
    category: "settings",
    keywords: ["פרופיל", "סיסמה", "אישי"],
    routes: ["/personal-area"],
    bodyHtml: ul(["עדכון שם ופרטים.", "החלפת סיסמה.", "מצב תצוגה (בהיר/כהה) נשמר אצלכם בלבד."]),
  },
];

export function articleRoutesMatch(article: HelpArticle, pathname: string): boolean {
  return article.routes.some((r) => (r === "/" ? pathname === "/" : pathname === r || pathname.startsWith(`${r}/`)));
}
